import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import * as yup from 'yup';
import * as projectApi from '../../../lib/api/project';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';

import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import {
  Box,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import BootstrapDialog, {
  BootstrapDialogTitle,
} from '../../../components/common/dialog/BootstrapDialog';
import OrderSheetForm from '../../../components/projectmng/order_sheet_sent/OrderSheetForm';
import DetailTemplate from '../../../components/common/template/DetailTemplate';
import OrderSheetDetails from '../../../components/projectmng/order_sheet_sent/OrderSheetDetails';
import OrderSheetPreviewModal from '../../../components/projectmng/order_sheet_sent/OrderSheetPreviewModal';
import { listOrderSheetSents } from '../../../modules/projectModule/orderSheetSents';

const OrderSheetModalContainer = ({
  project,
  costs,
  type,
  selectedOrderSheet,
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [showPreview, setShowPreview] = useState(false);
  const handleClosePreview = () => {
    setShowPreview(false);
  };

  const handleOpenPreview = () => {
    setShowPreview(true);
  };

  const [sellPartnerOptions, setSellPartnerOptions] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);

  useEffect(() => {
    if (costs) {
      const options = _.uniqBy(costs, 'sellPartnerUuid');
      setSellPartnerOptions(options);
    }
  }, [costs]);

  const getInitialValues = () => ({
    sellPartnerUuid: '',
    orderSheetNo: dayjs().format('YYYYMMDDHHmmss'),
    sheetCrtDate: new Date(),
    sheetUsrNm: '',
    sheetUsrTel: '',
    sheetCstNm: project?.cstNm,
    sheetPayCond: '세금계산서 발행 후 30일',
    sheetPeriod: '발주 후 즉시',
    sheetDesc: '',
  });

  const [initialValues, setInitialValues] = useState({
    sellPartnerUuid: '',
    orderSheetNo: dayjs().format('YYYYMMDDHHmmss'),
    sheetCrtDate: new Date(),
    sheetUsrNm: '',
    sheetUsrTel: '',
    sheetCstNm: project?.cstNm,
    sheetPayCond: '세금계산서 발행 후 30일',
    sheetPeriod: '발주 후 즉시',
    sheetDesc: '',
  });

  const validationSchema = yup.object({
    sellPartnerUuid: yup.string().required('매입처를 선택하세요'),
    orderSheetNo: yup.string().required('발주번호를 입력하세요'),
    sheetCrtDate: yup
      .date()
      .required('발주입자를 입력하세요')
      .nullable()
      .default(undefined),
    sheetUsrNm: yup.string().required('담당자명을 입력하세요'),
    sheetUsrTel: yup.string().required('담당자 연락처를 입력하세요'),
    sheetCstNm: yup.string().required('사업명을 입력하세요'),
    sheetPayCond: yup.string().required('결제 조건을 입력하세요'),
    sheetPeriod: yup.string().required('납기를 입력하세요'),
  });

  useEffect(() => {
    if (type === 'edit' && selectedOrderSheet) {
      const copySheet = _.clone(selectedOrderSheet);
      const init = {
        ...copySheet,
      };
      //console.log(init, 'init');
      setInitialValues(init);
    } else {
      setInitialValues(getInitialValues());
    }
  }, [type, selectedOrderSheet]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSave(values);

      setSubmitting(false);
      handleClose();
    },
  });

  const onSave = async (values) => {
    const dataToSubmit = {
      prjUuid: project?.prjUuid,
      sellPartnerUuid: values.sellPartnerUuid,
      orderSheetNo: values.orderSheetNo,
      sheetCrtDate: dayjs(values.sheetCrtDate).format('YYYY-MM-DD'),
      sheetUsrNm: values.sheetUsrNm,
      sheetUsrTel: values.sheetUsrTel,
      sheetCstNm: values.sheetCstNm,
      sheetPayCond: values.sheetPayCond,
      sheetPeriod: values.sheetPeriod,
      sheetDesc: values.sheetDesc,
      sheetCostDetails: orderDetails,
    };
    //console.log(JSON.stringify(dataToSubmit));

    if (type === 'add') {
      try {
        const res = await projectApi.addOrderSheet(dataToSubmit);

        if (res.data) {
          dispatch(listOrderSheetSents(project?.prjUuid));
        }
        enqueueSnackbar('보낸 발주서 등록 성공', {
          variant: 'success',
        });
      } catch (err) {
        let message =
          typeof err.response !== 'undefined'
            ? err.response?.estimate?.message
            : err.message;
        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    } else {
      dataToSubmit.spoUuid = selectedOrderSheet?.spoUuid;
      try {
        const res = await projectApi.editOrderSheet(dataToSubmit);

        if (res.data) {
          dispatch(listOrderSheetSents(project?.prjUuid));
          enqueueSnackbar('보낸 발주서 수정 성공', {
            variant: 'success',
          });
        }
      } catch (err) {
        let message =
          typeof err.response !== 'undefined'
            ? err.response?.estimate?.message
            : err.message;
        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    }
  };

  const handleDelete = async () => {
    //console.log(data.prjUuid, data.spoUuid);
    try {
      const res = await projectApi.removeOrderSheet(
        selectedOrderSheet.prjUuid,
        selectedOrderSheet.spoUuid,
      );
      if (res.data) {
        dispatch(listOrderSheetSents(project?.prjUuid));

        enqueueSnackbar('보낸 발주서 삭제 성공', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
    handleClose();
  };

  useEffect(() => {
    if (formik.values?.sellPartnerUuid !== '') {
      const filtered = _.filter(costs, (cost) => {
        return cost.sellPartnerUuid === formik.values.sellPartnerUuid;
      });
      setOrderDetails(filtered);
    }
  }, [formik.values?.sellPartnerUuid]);

  const handleClose = () => {
    formik.resetForm({ values: getInitialValues() });

    onClose();
  };

  //엑셀 다운로드
  //파일명 확인
  const downloadFile = (file, filename, filedoc) => {
    let blob = new Blob([file.data]);
    const fileName = `${filename}.${filedoc}`;

    if (window.navigator.msSaveOrOpenBlob) {
      // IE 10+는 해당 함수 제공
      window.navigator.msSaveOrOpenBlob(blob, fileName);
    } else {
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.target = '_self';
      if (fileName) link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(link); //메모리 누수 방지
    }
  };

  const handleExcel = async () => {
    try {
      const blob = await projectApi.downloadOrderSheet(
        selectedOrderSheet.prjUuid,
        selectedOrderSheet.spoUuid,
      );
      const filename = blob.headers['filename'];
      downloadFile(blob, decodeURIComponent(filename), 'xlsx');
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  return (
    <BootstrapDialog
      open={open}
      onClose={handleClose}
      aria-labelledby="draggable-ordersheet-dialog"
      fullWidth={true}
      maxWidth="lg"
    >
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <BootstrapDialogTitle
          style={{ cursor: 'move' }}
          id="draggable-ordersheet-dialog"
          onClose={handleClose}
        >
          발주서 {type === 'add' ? '작성' : '수정'}
        </BootstrapDialogTitle>
        <DialogContent
          dividers
          sx={{
            height: 500,
            overflowY: 'auto',
          }}
        >
          <OrderSheetForm
            formik={formik}
            sellPartnerOptions={sellPartnerOptions}
          />
          {formik.values.sellPartnerUuid &&
          formik.values.sellPartnerUuid !== '' ? (
            <DetailTemplate title="발주 상세 내역">
              <OrderSheetDetails costs={orderDetails} />
            </DetailTemplate>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Typography variant="subtitle2">
                매입처 선택 후 상세내역이 표시됩니다.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            {type === 'edit' && (
              <Box sx={{ mx: 1 }}>
                <Button
                  color="error"
                  variant="contained"
                  onClick={handleDelete}
                >
                  삭제
                </Button>
              </Box>
            )}
            <Box sx={{ mx: 1 }}>
              <Button
                variant="contained"
                color="info"
                onClick={handleOpenPreview}
                disabled={!(formik.dirty && formik.isValid)}
              >
                미리보기
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            {type === 'edit' && (
              <Box sx={{ mx: 1 }}>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={handleExcel}
                >
                  엑셀 다운로드
                </Button>
              </Box>
            )}
            <Box>
              <Button type="submit" color="primary" variant="contained">
                저장
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Box>
      {showPreview && (
        <OrderSheetPreviewModal
          type={type}
          project={project}
          orderSheet={formik.values}
          orderDetails={orderDetails}
          open={showPreview}
          handleClose={handleClosePreview}
        />
      )}
    </BootstrapDialog>
  );
};

export default OrderSheetModalContainer;
